import { GridViewConfig } from 'src/pages/Hindsighting/StyleColorReview/GridView/GridView.slice';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Pivot, BasicPivotItem, TimeEntry } from 'src/worker/pivotWorker.types';
import { ErrorBoundaryComponentError } from '../ErrorBoundary/ErrorBoundary.slice';
import { ViewDataState } from 'src/types/Domain';
import { isNil } from 'lodash';

export interface VisualizeSlice {
  vizViewDefns?: GridViewConfig; // TODO
  isConfigLoading: boolean;
  isDataLoading: boolean;
  data?: Pivot;
  selectedDataId?: string;
  timeEntries: TimeEntry[];
  planCurrent?: string;
  summaryData: BasicPivotItem[] | undefined;
  /** MetricLineGraph state, loaded the same way ConfigurableGrid loads its graph: the graph is a
   * presentational component and its data/categories come from here (see `fetchVisualizeLineGraphData`).
   * The graph's own viewDefn is already in `vizViewDefns.graphs`, so it is not duplicated here. */
  graphState: ViewDataState;
  graphData: BasicPivotItem[][][] | undefined;
  graphCategories: string[] | undefined;
  /** The topMembers the graph data was (or will be) fetched for. */
  graphSelectedId: string[] | undefined;
}

const initialState: VisualizeSlice = {
  isConfigLoading: false,
  isDataLoading: false,
  timeEntries: [],
  summaryData: undefined,
  graphState: ViewDataState.idle,
  graphData: undefined,
  graphCategories: undefined,
  graphSelectedId: undefined,
};

const visualizeSliceReducer = createSlice({
  name: 'visualize',
  initialState: initialState,
  reducers: {
    requestVisualizeConfig: (state) => {
      state.isConfigLoading = true;
    },
    receiveVisualizeConfig: (state, action: PayloadAction<GridViewConfig>) => {
      state.vizViewDefns = action.payload;
      state.isConfigLoading = false;
    },
    requestVisualizeData: (state, action: PayloadAction<string>) => {
      state.isDataLoading = true;
      state.data = undefined;
      state.selectedDataId = action.payload;
    },
    receivedVisualizeData: (state, action: PayloadAction<Pivot | undefined>) => {
      state.isDataLoading = false;
      state.data = action.payload;
    },
    receivedVisualizeMacroData: (state, action: PayloadAction<BasicPivotItem[]>) => {
      state.summaryData = action.payload;
    },
    // Triggers/marks a line-graph fetch (see `fetchVisualizeLineGraphData`). An optional `selectedId`
    // updates the stored selection.
    requestVisualizeGraphData: (state, action: PayloadAction<{ selectedId: string[] } | undefined>) => {
      if (!isNil(action.payload?.selectedId)) {
        state.graphSelectedId = action.payload!.selectedId;
      }
      state.graphState = ViewDataState.liveDataLoadingNoCache;
      state.graphData = undefined;
    },
    receiveVisualizeGraphData: (state, action: PayloadAction<{ data: BasicPivotItem[][][]; categories: string[] }>) => {
      state.graphState = ViewDataState.liveDataReady;
      state.graphData = action.payload.data;
      state.graphCategories = action.payload.categories;
    },
    receiveError(_state, _action: PayloadAction<ErrorBoundaryComponentError>) {
      return initialState;
    },
  },
});

export const {
  requestVisualizeConfig,
  receiveVisualizeConfig,
  requestVisualizeData,
  receivedVisualizeData,
  receivedVisualizeMacroData,
  requestVisualizeGraphData,
  receiveVisualizeGraphData,
  receiveError,
} = visualizeSliceReducer.actions;

export default visualizeSliceReducer.reducer;
